import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IFilter, IGridResult } from 'src/app/core/models/base';
import { IWeekReportingObject, IWeekReportingObjectSearchRequest } from '../../models/week-reporting-object';
import { TimeEntryService } from '../../services/time-entry.service';
import { UntypedFormBuilder } from '@angular/forms';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { IEmployee } from 'src/app/employee/employee';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { IDayReportingObject, IDayReportingObjectSearchRequest, IExportDayReportingObjectRequest } from '../../models/day-reporting-object';
import { ISearchTimeEntriesRequest, ITimeEntry } from '../../models/time-entry';
import { ITimeEntryCategory } from '../../models/time-entry-category';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { ActivatedRoute } from '@angular/router';

type ViewMode = 'week' | 'day' | 'entry';

@UntilDestroy()
@Component({
    selector: 'app-time-report',
    templateUrl: './time-report.component.html',
    styleUrls: ['./time-report.component.css'],
    standalone: false
})
export class TimeReportComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private fb: UntypedFormBuilder,
    private tenantSettingsStore: TenantSettingsStoreService,
    private route: ActivatedRoute
  ) { }

  weekReportingObjectsGridData: IGridResult<IWeekReportingObject[]> = {
    data : [],
    total: 0
  };

  // Flat list of day reports for the daily view.
  dayReportingObjectsGridData: IDayReportingObject[] = [];

  // Single time entries for the entry view.
  timeEntriesGridData: IGridResult<ITimeEntry[]> = {
    data: [],
    total: 0
  };

  timeEntryCategories: ITimeEntryCategory[] = [];

  // View toggle between the weekly ('week'), the daily ('day') and the single entry ('entry') report view.
  viewMode: ViewMode = 'entry';

  // Footer totals per view: page total + grand total over the whole selection.
  weekTotalWorkTime: string;
  weekTotalFlexTime: string;
  weekTotalPreWorkTime: string;
  weekGrandWorkTime: string;
  weekGrandFlexTime: string;
  weekGrandPreWorkTime: string;
  dayTotalWorkTime: string;
  dayTotalFlexTime: string;
  dayTotalPreWorkTime: string;
  dayGrandWorkTime: string;
  dayGrandFlexTime: string;
  dayGrandPreWorkTime: string;
  dayTotalCount: number = 0;
  entryTotalTime: string;
  entryGrandTotalTime: string;

  // Only the employees whose reports the current user may open — the server refuses the rest.
  public employees$ : Observable<IEmployee[]> =
    this.timeEntryService.getVisibleEmployees().pipe(shareReplay({ bufferSize: 1, refCount: false }))

  // A default item without an id sends no filter value, which makes the backend return
  // every row. The dropdowns surface that state; their label is rendered by the template
  // so that it follows the active language.
  employeeDefaultItem = { id: null, firstName: null, lastName: null };
  timeEntryCategoryDefaultItem = { id: null, name: null };

  searchRequest: IWeekReportingObjectSearchRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy:['StartDate desc','EmployeeId']
  }

  daySearchRequest: IDayReportingObjectSearchRequest = {
    employeeId: null,
    startDate: null,
    endDate: null,
    showOnlySignable: false,
    pageNumber: 1,
    pageSize: 25,
    orderBy: ['Date desc']
  }

  entrySearchRequest: ISearchTimeEntriesRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy: ['Date desc','Start']
  }

  filterForm = this.fb.group({
    dateRangeStart:[],
    dateRangeEnd:[],
    employee: [null],
    showOnlySignable:[false],
    timeEntryCategory: [null],
    assignmentId: [null]
  })

  // The search buttons show their spinner for the endpoint of the active view.
  get searchLoadingUrl(): string {
    switch(this.viewMode){
      case 'day': return '/TimeEntry/searchDayReports';
      case 'entry': return '/TimeEntry/searchTimeEntries';
      default: return '/TimeEntry/searchWeekReports';
    }
  }

  vm$ = combineLatest([
    this.tenantSettingsStore.tenantSettings$
  ]).pipe(
    map(([
      tenantSettings
    ]) => ({
      tenantSettings
    }))
  );

  ngOnInit(): void {
    this.timeEntryCategories = this.route.snapshot.data.timeEntryCategories as ITimeEntryCategory[];

    this.updateList();
  }

  updateList(){
    if(this.viewMode === 'day'){
      this.updateDayList();
    } else if(this.viewMode === 'entry'){
      this.updateEntryList();
    } else {
      this.updateWeekList();
    }
  }

  updateWeekList(){
    this.timeEntryService.searchWeekReports(this.searchRequest)
    .pipe(
      untilDestroyed(this),
      map(response => {
        const tmp = response;

        tmp.data.map(
          weekReportObject => {

            weekReportObject.dayReportingObjects = weekReportObject.dayReportingObjects.map((dayReportingObject: IDayReportingObject) => {
              dayReportingObject.date = new Date(dayReportingObject.date);
              return dayReportingObject;
            });

            weekReportObject.dayReportingObjects = weekReportObject.dayReportingObjects.sort((a, b) => b.date.getTime()- a.date.getTime());

            weekReportObject.dayReportingObjects.map(dayReportObject => {
              let totalDuration = new TimeSpan();

              dayReportObject.timeEntries = dayReportObject.timeEntries.sort((a,b) => {
                const timeSpanA = TimeSpan.parse(a.start).getTotalSeconds();
                const timeSpanB = TimeSpan.parse(b.start).getTotalSeconds();

                return timeSpanA - timeSpanB;
              })

              dayReportObject.timeEntries.forEach(timeEntry => {
                if(timeEntry.duration){
                  const timeSpan = TimeSpan.parse(timeEntry.duration)
                  totalDuration = totalDuration.add(timeSpan)
                }
              });

              dayReportObject.duration = totalDuration.toStringShort();

              dayReportObject.flexTimeChangeObj = TimeSpan.parse(dayReportObject.flexTimeChange ?? "00:00:00").getAsTimeObject();
              dayReportObject.preWorkTimeChangeObj = TimeSpan.parse(dayReportObject.preWorkTimeChange ?? "00:00:00").getAsTimeObject();
              dayReportObject.overtimeChangeObj = TimeSpan.parse(dayReportObject.overtimeChange ?? "00:00:00").getAsTimeObject();
            });

            weekReportObject.overtimeObj = TimeSpan.parse(weekReportObject.overtime ?? "00:00:00").getAsTimeObject();
            weekReportObject.flexTimeChangeObj = TimeSpan.parse(weekReportObject.flexTimeChange).getAsTimeObject();
            weekReportObject.preWorkTimeChangeObj = TimeSpan.parse(weekReportObject.preWorkTimeChange).getAsTimeObject();
            weekReportObject.timeSupplementTimeChangeObj = TimeSpan.parse(weekReportObject.timeSupplementTimeChange).getAsTimeObject();
            weekReportObject.totalTimeWorkedObj = TimeSpan.parse(weekReportObject.totalTimeWorked).getAsTimeObject();
          }
        )

        return tmp;
      })
      )
    .subscribe(response => {
      this.weekReportingObjectsGridData.data = response.data;
      this.weekReportingObjectsGridData.total = response.totalCount;

      // Page totals sum the visible week rows.
      this.weekTotalWorkTime = this.sumTimes(response.data.map(week => week.totalTimeWorked));
      this.weekTotalFlexTime = this.sumTimes(response.data.map(week => week.flexTimeChange));
      this.weekTotalPreWorkTime = this.sumTimes(response.data.map(week => week.preWorkTimeChange));

      // Grand totals over the whole selection come from the backend (all pages).
      this.weekGrandWorkTime = response.totalTimeWorked;
      this.weekGrandFlexTime = response.flexTimeChange;
      this.weekGrandPreWorkTime = response.preWorkTimeChange;
    })
  }

  updateDayList(){
    this.timeEntryService.searchDayReports(this.daySearchRequest)
    .pipe(
      untilDestroyed(this),
      map(response => {
        response.data.forEach(dayReportObject => {
          dayReportObject.date = new Date(dayReportObject.date);

          let totalDuration = new TimeSpan();

          dayReportObject.timeEntries = dayReportObject.timeEntries.sort((a, b) => {
            return TimeSpan.parse(a.start).getTotalSeconds() - TimeSpan.parse(b.start).getTotalSeconds();
          });

          dayReportObject.timeEntries.forEach(timeEntry => {
            if(timeEntry.duration){
              totalDuration = totalDuration.add(TimeSpan.parse(timeEntry.duration));
            }
          });

          dayReportObject.duration = totalDuration.toStringShort();

          dayReportObject.flexTimeChangeObj = TimeSpan.parse(dayReportObject.flexTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.preWorkTimeChangeObj = TimeSpan.parse(dayReportObject.preWorkTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.timeSupplementTimeChangeObj = TimeSpan.parse(dayReportObject.timeSupplementTimeChange ?? "00:00:00").getAsTimeObject();
          dayReportObject.overtimeChangeObj = TimeSpan.parse(dayReportObject.overtimeChange ?? "00:00:00").getAsTimeObject();
        });

        return response;
      })
    )
    .subscribe(response => {
      this.dayReportingObjectsGridData = response.data;
      this.dayTotalCount = response.totalCount;

      // Page totals (visible page) + grand totals over the whole selection (backend).
      this.dayTotalWorkTime = this.sumTimes(response.data.map(day => day.duration));
      this.dayTotalFlexTime = this.sumTimes(response.data.map(day => day.flexTimeChange));
      this.dayTotalPreWorkTime = this.sumTimes(response.data.map(day => day.preWorkTimeChange));

      this.dayGrandWorkTime = response.totalTimeWorked;
      this.dayGrandFlexTime = response.flexTimeChanges;
      this.dayGrandPreWorkTime = response.preWorkTimeChanges;
    })
  }

  updateEntryList(){
    this.timeEntryService.searchTimeEntries(this.entrySearchRequest)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.timeEntriesGridData.data = response.data;
      this.timeEntriesGridData.total = response.totalCount;

      // Page total (visible page) + grand total over the whole selection (backend).
      this.entryTotalTime = this.sumTimes(response.data.map(timeEntry => timeEntry.duration));
      this.entryGrandTotalTime = response.totalTime;
    })
  }

  private sumTimes(values: (string | undefined | null)[]): string {
    let total = new TimeSpan();
    values.forEach(value => {
      total = total.add(TimeSpan.parse(value ?? "00:00:00"));
    });
    return total.toString();
  }

  onViewModeChange(viewMode: ViewMode){
    this.viewMode = viewMode;

    // The grid of the incoming view is created fresh and starts on its first page,
    // so the search request has to start there as well.
    this.searchRequest.pageNumber = 1;
    this.daySearchRequest.pageNumber = 1;
    this.entrySearchRequest.pageNumber = 1;

    this.updateList();
  }

  onSubmitFilter(){
    const formValues = this.filterForm.value;
    const startDate = formValues.dateRangeStart
      ? new Date(formValues.dateRangeStart.getTime() - formValues.dateRangeStart.getTimezoneOffset() * 60000)
      : null;
    const endDate = formValues.dateRangeEnd
      ? new Date(formValues.dateRangeEnd.getTime() - formValues.dateRangeEnd.getTimezoneOffset() * 60000)
      : null;

    this.searchRequest.employeeId = formValues.employee?.id;
    this.searchRequest.startDate = startDate;
    this.searchRequest.endDate = endDate;
    this.searchRequest.showOnlySignable = formValues.showOnlySignable ?? false;
    this.searchRequest.pageNumber = 1;

    this.daySearchRequest.employeeId = formValues.employee?.id;
    this.daySearchRequest.startDate = startDate;
    this.daySearchRequest.endDate = endDate;
    this.daySearchRequest.showOnlySignable = formValues.showOnlySignable ?? false;
    this.daySearchRequest.pageNumber = 1;

    this.entrySearchRequest.employeeId = formValues.employee?.id;
    this.entrySearchRequest.dateFrom = startDate;
    this.entrySearchRequest.dateTo = endDate;
    this.entrySearchRequest.timeEntryCategoryId = formValues.timeEntryCategory?.id;
    this.entrySearchRequest.pageNumber = 1;
    this.setAssignmentFilter(formValues.assignmentId);

    this.updateList();
  }

  onResetFilter(){
    this.filterForm.reset();

    this.searchRequest.employeeId = null;
    this.searchRequest.pageNumber = 1;
    this.searchRequest.startDate = null;
    this.searchRequest.endDate = null;
    this.searchRequest.showOnlySignable = null;

    this.daySearchRequest.employeeId = null;
    this.daySearchRequest.pageNumber = 1;
    this.daySearchRequest.startDate = null;
    this.daySearchRequest.endDate = null;
    this.daySearchRequest.showOnlySignable = null;

    this.entrySearchRequest.employeeId = null;
    this.entrySearchRequest.pageNumber = 1;
    this.entrySearchRequest.dateFrom = null;
    this.entrySearchRequest.dateTo = null;
    this.entrySearchRequest.timeEntryCategoryId = null;
    this.setAssignmentFilter(null);

    this.updateList();
  }

  // The time entry search only knows the generic advanced filter, so the assignment
  // is mapped onto it instead of onto a dedicated request property.
  private setAssignmentFilter(assignmentId: string | null){
    if(assignmentId){
      const filter: IFilter = {
        field: 'assignment.id',
        operator: 'eq',
        value: assignmentId
      }
      this.entrySearchRequest.advancedFilter = filter;
    } else {
      delete this.entrySearchRequest['advancedFilter'];
    }
  }

  onExport(){
    const request: IExportDayReportingObjectRequest = {
      employeeId: this.daySearchRequest.employeeId,
      startDate: this.daySearchRequest.startDate,
      endDate: this.daySearchRequest.endDate
    };

    this.timeEntryService.exportDevices(request)
    .pipe(untilDestroyed(this))
    .subscribe(response => downloadBlob(response, "TagesrapporteExport.xlsx"));
  }

}
